import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import _Object$assign from '@babel/runtime-corejs3/core-js-stable/object/assign';
import { forwardRef } from 'react';
import { c as classNames } from '../../shared/bind-a2a3a613.js';
import { deprecationWarning } from '../../utils/warning.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Icon } from '../Icon/Icon.js';
import { Loader } from '../Loader/Loader.js';

var styles = {
  "unit-base": "4",
  "button": "web_ui__Button__button",
  "inline": "web_ui__Button__inline",
  "disabled": "web_ui__Button__disabled",
  "content": "web_ui__Button__content",
  "label": "web_ui__Button__label",
  "truncated": "web_ui__Button__truncated",
  "icon": "web_ui__Button__icon",
  "loader": "web_ui__Button__loader",
  "with-loader": "web_ui__Button__with-loader",
  "default": "web_ui__Button__default",
  "without-text": "web_ui__Button__without-text",
  "icon-left": "web_ui__Button__icon-left",
  "icon-right": "web_ui__Button__icon-right",
  "medium": "web_ui__Button__medium",
  "small": "web_ui__Button__small",
  "outlined": "web_ui__Button__outlined",
  "filled": "web_ui__Button__filled",
  "flat": "web_ui__Button__flat",
  "inverse": "web_ui__Button__inverse",
  "primary": "web_ui__Button__primary",
  "amplified": "web_ui__Button__amplified",
  "muted": "web_ui__Button__muted",
  "warning": "web_ui__Button__warning",
  "success": "web_ui__Button__success",
  "expose": "web_ui__Button__expose"
};

var _excluded = ["type", "styling", "size", "inline", "theme", "truncated", "disabled", "inverse", "iconPosition", "icon", "iconName", "iconColor", "isLoading", "jsClass", "children", "text", "url", "aria", "forwardedRef", "urlProps", "onClick", "testId"];
var BUTTON_STYLING_VALUES = ['outlined', 'filled', 'flat'];
var BUTTON_ICON_POSITION_VALUES = ['left', 'right'];
var BUTTON_TYPE_VALUES = ['button', 'submit', 'reset'];
var BUTTON_SIZE_VALUES = ['default', 'medium', 'small'];
/* ---------- Back-compat shims (public API) ---------- */
var BUTTON_STYLING = {
  Outlined: 'outlined',
  Filled: 'filled',
  Flat: 'flat'
};
var BUTTON_ICON_POSITION = {
  Left: 'left',
  Right: 'right'
};
var BUTTON_TYPE = {
  Button: 'button',
  Submit: 'submit',
  Reset: 'reset'
};
var BUTTON_SIZE = {
  Default: 'default',
  Medium: 'medium',
  Small: 'small'
};

/* ---------- Type definitions ---------- */

var cssClasses = classNames.bind(styles);
var Button = function Button(_ref) {
  var _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    _ref$styling = _ref.styling,
    styling = _ref$styling === void 0 ? 'outlined' : _ref$styling,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'default' : _ref$size,
    inline = _ref.inline,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? 'primary' : _ref$theme,
    _ref$truncated = _ref.truncated,
    truncated = _ref$truncated === void 0 ? true : _ref$truncated,
    disabled = _ref.disabled,
    inverse = _ref.inverse,
    _ref$iconPosition = _ref.iconPosition,
    iconPosition = _ref$iconPosition === void 0 ? 'left' : _ref$iconPosition,
    icon = _ref.icon,
    iconName = _ref.iconName,
    iconColor = _ref.iconColor,
    isLoading = _ref.isLoading,
    jsClass = _ref.jsClass,
    children = _ref.children,
    _ref$text = _ref.text,
    text = _ref$text === void 0 ? '' : _ref$text,
    url = _ref.url,
    aria = _ref.aria,
    forwardedRef = _ref.forwardedRef,
    urlProps = _ref.urlProps,
    onClick = _ref.onClick,
    testId = _ref.testId,
    elementProps = _objectWithoutProperties(_ref, _excluded);
  var renderIcon = function renderIcon() {
    if (icon) {
      return /*#__PURE__*/jsx("span", {
        className: styles.icon,
        children: icon
      });
    }
    if (iconName) {
      return /*#__PURE__*/jsx("span", {
        className: styles.icon,
        children: /*#__PURE__*/jsx(Icon, {
          name: iconName,
          color: iconColor
        })
      });
    }
    return null;
  };
  var renderLoader = function renderLoader() {
    if (!isLoading) return null;
    return /*#__PURE__*/jsx("div", {
      className: styles.loader,
      children: /*#__PURE__*/jsx(Loader, {
        size: size,
        theme: styling === 'filled' && !inverse ? null : theme
      })
    });
  };
  var contentText = children || text;
  var isDisabled = disabled || isLoading;
  var buttonClass = cssClasses('button', styling, size, theme, jsClass, _defineProperty(_defineProperty(_defineProperty({
    inline: inline,
    inverse: inverse,
    disabled: isDisabled,
    truncated: truncated
  }, "icon-".concat(iconPosition), icon || iconName), 'without-text', !contentText), 'with-loader', isLoading));
  var content = /*#__PURE__*/jsxs("span", {
    className: styles.content,
    children: [contentText ? /*#__PURE__*/jsx("span", {
      className: styles.label,
      children: contentText
    }) : null, renderIcon(), renderLoader()]
  });
  if (url) {
    var className = cssClasses(buttonClass, urlProps === null || urlProps === void 0 ? void 0 : urlProps.className);
    return (
      /*#__PURE__*/
      // @ts-expect-error - spreading props that may contain invalid a-tag fields (e.g. ref)
      jsx("a", _objectSpread(_objectSpread(_objectSpread(_objectSpread({
        "aria-busy": isLoading || undefined,
        "aria-disabled": isDisabled || undefined
      }, elementProps), aria), urlProps), {}, {
        href: url,
        className: className,
        "data-testid": testId,
        onClick: onClick,
        children: content
      }))
    );
  }
  return /*#__PURE__*/jsx("button", _objectSpread(_objectSpread(_objectSpread({
    // eslint-disable-next-line react/button-has-type
    type: type,
    "aria-busy": isLoading || undefined
  }, elementProps), aria), {}, {
    className: buttonClass,
    ref: forwardedRef,
    onClick: onClick,
    "data-testid": testId,
    disabled: isDisabled,
    children: content
  }));
};

/** The public component type: a React component value that also has deprecated statics */

// BUG: default exports do not auto-generate storybook ArgsTable
// https://github.com/storybookjs/storybook/issues/9511
// remove export when resolved
var ButtonWithForwardedRefBase = /*#__PURE__*/forwardRef(function (props, ref) {
  if (props.jsClass) {
    deprecationWarning('Property jsClass on Button component has been deprecated.');
  }
  return /*#__PURE__*/jsx(Button, _objectSpread({
    forwardedRef: ref
  }, props));
});
ButtonWithForwardedRefBase.displayName = Button.name;

/** Exported component: arrow fn + statics, deprecations intact, docgen-friendly */
var ButtonWithForwardedRef = _Object$assign(ButtonWithForwardedRefBase, {
  /** @deprecated Use string unions, e.g. styling="filled" */
  Styling: BUTTON_STYLING,
  /** @deprecated Use string unions, e.g. iconPosition="left" */
  IconPosition: BUTTON_ICON_POSITION,
  /** @deprecated Use string unions, e.g. type="submit" */
  Type: BUTTON_TYPE,
  /** @deprecated Use string unions, e.g. size="small" */
  Size: BUTTON_SIZE
});

export { BUTTON_ICON_POSITION, BUTTON_ICON_POSITION_VALUES, BUTTON_SIZE, BUTTON_SIZE_VALUES, BUTTON_STYLING, BUTTON_STYLING_VALUES, BUTTON_TYPE, BUTTON_TYPE_VALUES, ButtonWithForwardedRef, ButtonWithForwardedRef as default };
//# sourceMappingURL=Button.js.map
